/*
 * The sticky bar reports the live state of the main product form rather than
 * carrying a second control and a second <form> that could drift from it.
 *
 * One product form exists on the page; this file is a read-only mirror of it, so
 * every piece of state here is derived from the form and never asserted. The
 * previous version derived it from `mainSubmitButton.disabled && !gateOpen`,
 * which is not a state at all: it is true for "choose options" (recoverable),
 * "unavailable" (terminal) and "sold out" (terminal) alike, and the `&& !gateOpen`
 * term forced `disabled` to `false` for the two terminal cases -- producing a
 * dimmed, `cursor: not-allowed` button that scrolled back to the picker on click.
 */

/*
 * The mutually exclusive states this bar can be in. Exactly one is active at a
 * time and it alone decides `disabled`, `aria-disabled`, the mirrored label, the
 * data attribute the stylesheet keys off, and whether a click forwards the add
 * or navigates to the picker.
 *
 *   ready        the form can be submitted; the click is forwarded
 *   choose       options are outstanding; recoverable, so the button stays
 *                enabled and the click goes to the picker
 *   unavailable  the chosen combination does not exist; terminal, disabled
 *   sold-out     the chosen combination is out of stock; terminal, disabled
 *   error        the last add failed. Operable -- `product-form` clears its own
 *                `aria-disabled` in `finally` unless it kept it for a sold-out
 *                message, so a retry from the bar must keep working. Only the
 *                reason is different, and that lives in the notice.
 *   loading      an add is in flight; focusable so a keyboard user does not
 *                lose their place mid-request
 */
const STICKY_ATC_STATE = {
  READY: 'ready',
  CHOOSE: 'choose',
  UNAVAILABLE: 'unavailable',
  SOLD_OUT: 'sold-out',
  ERROR: 'error',
  LOADING: 'loading',
};

/* States the shopper cannot act on. These -- and only these -- get `disabled`. */
const STICKY_ATC_TERMINAL = new Set([STICKY_ATC_STATE.UNAVAILABLE, STICKY_ATC_STATE.SOLD_OUT]);

class StickyATC {
  static init() {
    document.querySelectorAll('[data-sticky-atc]').forEach((element) => new StickyATC(element).init());
  }

  constructor(element) {
    this.element = element;
  }

  init() {
    this.mainSubmitButton = null;
    this.productInfo = null;
    this.mainPrice = null;
    this.mainErrorWrapper = null;
    this.price = this.element.querySelector('[data-price]');
    this.variantTitle = this.element.querySelector('[data-variant-title]');
    this.submitButton = this.element.querySelector('[data-sticky-atc-submit]');
    this.submitLabel = this.element.querySelector('[data-sticky-atc-label]');
    this.submitSpinner = this.submitButton && this.submitButton.querySelector('.loading__spinner');
    this.noticeRow = this.element.querySelector('[data-sticky-atc-notice-row]');
    this.notice = this.element.querySelector('[data-sticky-atc-notice]');
    this.noticeLabel = this.element.querySelector('[data-sticky-atc-notice-label]');
    this.noticeDetail = this.element.querySelector('[data-sticky-atc-notice-detail]');
    this.noticeAction = this.element.querySelector('[data-sticky-atc-notice-action]');
    this.currentVariantTitle = '';
    this.state = null;
    this.cartErrorFallback = '';

    // There is one product form on the page. This bar forwards to it rather than
    // submitting a second one, so the variant, the error handling and the cart
    // drawer behaviour can never diverge from the main buy button.
    if (!this.submitButton || !this.bindProductInfo()) {
      this.element.remove();
      return;
    }

    this.currentVariantTitle = this.readVariantTitle();
    this.syncVariantTitle();

    this.submitButton.addEventListener('click', () => this.onSubmitClick());
    // In the terminal states the submit button is genuinely `disabled` and cannot
    // receive the click that would take the shopper back to the picker, so the
    // notice carries that route instead.
    this.noticeAction?.addEventListener('click', () => this.scrollToOptions());

    this.variantChangeUnsubscriber = subscribe(PUB_SUB_EVENTS.variantChange, (event) => {
      const variant = event.data && event.data.variant;
      // Remember the resolved title even while the gate is closed, so it is ready the moment
      // the shopper finishes choosing and the gate lifts.
      if (variant && this.variantTitle) this.currentVariantTitle = variant.title;
      // A variant change means the shopper has moved on from whatever failed; the
      // remembered failure would otherwise pin the bar in `error` for the rest of
      // the visit, since nothing upstream ever clears it.
      this.cartErrorFallback = '';
      this.syncVariantTitle();
      this.syncSubmitState();
    });

    /*
     * A failed add is not always visible in the form: `product-form.js` only
     * writes the reason into `.product-form__error-message-wrapper` on a JSON
     * error response, and a network failure reaches nothing but
     * `dispatchCartErrorEvent`. Both are caught here so the bar cannot sit on a
     * cheerful "Add to cart" after the request failed.
     *
     * The message is recorded rather than rendered on the spot: `product-form.js`
     * publishes `cartError` *before* it calls `handleErrorMessage()`, so
     * rendering synchronously from the event would announce the generic string
     * and then be overwritten by the specific one the form's own `role="alert"`
     * is about to speak. A microtask runs after that whole synchronous block.
     */
    this.cartErrorUnsubscriber = subscribe(PUB_SUB_EVENTS.cartError, (event) => {
      this.cartErrorFallback = event?.data?.errors || window.cartStrings?.error || '';
      queueMicrotask(() => this.syncSubmitState());
    });

    /*
     * `product-info.js` re-renders by replacing the whole `<product-info>` node
     * (`HTMLUpdateUtility.viewTransition`), which swaps in a new submit button,
     * a new price block and a new error wrapper while the old subtree is hidden
     * for 500ms and then removed. Cached references therefore expire on the very
     * first option change, and an observer left pointing at the detached node
     * silently stops firing -- which is why the bar used to keep reporting
     * whatever the server rendered on page load. Re-resolve on every `loaded`
     * event; `product-info` dispatches it from `connectedCallback`, which runs
     * when the replacement node is inserted.
     */
    document.addEventListener('product-info:loaded', () => this.bindProductInfo());
  }

  /*
   * Points every read and observer at the *current* `<product-info>` subtree.
   * Returns false only when there is no usable product form on the page at all.
   */
  bindProductInfo() {
    const button = document.querySelector('main [id^="ProductSubmitButton-"]');
    if (!button || !button.isConnected) return false;
    if (button === this.mainSubmitButton) return true;

    const productInfo = document.querySelector('main product-info');
    this.mainSubmitButton = button;
    this.productInfo = productInfo;

    // Scope the price lookup to this product-info's section when it can be
    // determined: a bare `main [id^="price-"]` can resolve to another price
    // block on the page, and a stale one no longer mirrors anything.
    const section = productInfo?.dataset.section;
    this.mainPrice = section ? document.getElementById(`price-${section}`) : null;
    if (!this.mainPrice) this.mainPrice = document.querySelector('main [id^="price-"]');
    this.mainErrorWrapper = productInfo?.querySelector('.product-form__error-message-wrapper') || null;

    /*
     * Observe the button *subtree*, not just its attributes. Every label mutation
     * today happens to coincide with a `disabled` toggle, but nothing declares
     * that coupling -- `toggleSubmitButton` can be called with only a new string,
     * and a label-only change would desync the bar silently. Scoped to this one
     * button rather than the document, so it stays cheap.
     */
    this.buttonObserver?.disconnect();
    this.buttonObserver = new MutationObserver(() => this.syncSubmitState());
    this.buttonObserver.observe(button, {
      attributes: true,
      attributeFilter: ['disabled', 'aria-disabled', 'class'],
      childList: true,
      subtree: true,
      characterData: true,
    });

    // The form's own `role="alert"` wrapper is the reason the shopper needs after
    // a failed add, so it is read rather than re-derived.
    this.errorObserver?.disconnect();
    if (this.mainErrorWrapper) {
      this.errorObserver = new MutationObserver(() => this.syncSubmitState());
      this.errorObserver.observe(this.mainErrorWrapper, {
        attributes: true,
        attributeFilter: ['hidden'],
        childList: true,
        subtree: true,
        characterData: true,
      });
    }

    // Mirror the rendered price rather than formatting money in JS: currency,
    // locale and sale/compare-at markup are already correct upstream.
    this.priceObserver?.disconnect();
    if (this.mainPrice && this.price) {
      this.priceObserver = new MutationObserver(() => this.syncPrice());
      this.priceObserver.observe(this.mainPrice, { childList: true, subtree: true, characterData: true });
    }

    /*
     * The options gate toggles `data-options-gated` on `product-info`; mirror it
     * here so the bar never shows a variant the shopper has not actually chosen,
     * and so a gate change re-derives the state (opening the gate is what turns
     * `unavailable` back into `ready`).
     */
    this.gateObserver?.disconnect();
    if (productInfo) {
      this.gateObserver = new MutationObserver(() => {
        this.syncVariantTitle();
        this.syncSubmitState();
      });
      this.gateObserver.observe(productInfo, { attributes: true, attributeFilter: ['data-options-gated'] });
    }

    this.observer?.disconnect();
    // The bar is a shortcut back to the purchase controls, so it has no job while either the main
    // button or the option picker is on screen. It shows only once both have scrolled out of view.
    const inView = new Map();
    this.observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => inView.set(entry.target, entry.isIntersecting));
        const anyVisible = [...inView.values()].some(Boolean);
        this.element.classList.toggle('is-visible', !anyVisible);
      },
      { threshold: 0, rootMargin: '120px 0px 0px 0px' }
    );
    this.observer.observe(button);
    const pickerEl = productInfo?.querySelector('variant-selects, variant-radios');
    if (pickerEl) this.observer.observe(pickerEl);

    this.syncVariantTitle();
    this.syncPrice();
    this.syncSubmitState();
    return true;
  }

  onSubmitClick() {
    // `choose` is the only state that navigates: the main button is disabled
    // behind the gate, so forwarding the click would do nothing at all and leave
    // the shopper on "Choose options" with no route back. Everything the main
    // form can accept forwards to it -- including `error`, because
    // `product-form.onSubmitHandler` clears its own `aria-disabled` in `finally`
    // and a shopper whose add just failed must be able to retry from the bar
    // without scrolling back up. It ignores a second submit while one is pending,
    // so `loading` needs no guard of its own. The terminal states cannot be
    // clicked at all: they carry a real `disabled`.
    if (this.state === STICKY_ATC_STATE.CHOOSE) {
      this.scrollToOptions();
      return;
    }
    if (STICKY_ATC_TERMINAL.has(this.state)) return;
    // Clear the remembered failure before forwarding: `product-form` sets
    // `loading` synchronously inside its submit handler, so without this the bar
    // would snap straight back to `error` on the retry and never leave it.
    this.cartErrorFallback = '';
    this.mainSubmitButton.click();
  }

  get gateOpen() {
    return Boolean(document.querySelector('main product-info[data-options-gated]'));
  }

  /* The main button's rendered label, read the same way `product-form.js` writes it. */
  mainLabel() {
    return (this.mainSubmitButton?.querySelector('span')?.textContent || '').trim();
  }

  /* The reason the main form is showing, or '' when it is showing none. */
  formErrorMessage() {
    if (!this.mainErrorWrapper || this.mainErrorWrapper.hasAttribute('hidden')) return '';
    return (
      this.mainErrorWrapper.querySelector('.product-form__error-message')?.textContent.trim() || ''
    );
  }

  readSubmitState() {
    const button = this.mainSubmitButton;
    if (!button) return STICKY_ATC_STATE.READY;

    // 1. An add is in flight. `product-form` marks it with `loading` plus a
    //    transient `aria-disabled` and clears both in `finally`. This outranks the
    //    gate below: `data-options-gated` is still set from the pre-submit render,
    //    and a spinner must never be reported as a pending choice.
    if (button.classList.contains('loading')) return STICKY_ATC_STATE.LOADING;

    // 2. An add failed and the form is showing why. Ahead of the gate, because a
    //    stale `data-options-gated` must not mask a failure the shopper has to
    //    read. The form's `role="alert"` owns the announcement here, not the bar
    //    -- see `syncNotice`.
    if (this.formErrorMessage() || this.cartErrorFallback) return STICKY_ATC_STATE.ERROR;

    // 3. Options are still outstanding. Recoverable, so the button stays enabled
    //    and the click goes to the picker.
    if (this.gateOpen) return STICKY_ATC_STATE.CHOOSE;

    // 4. Terminal. `disabled` is the honest signal: it is what the server render
    //    (`buy-buttons.liquid`) and `product-info.setUnavailable` both set. Which
    //    terminal state it is cannot be read from the attribute -- both render
    //    `disabled` and differ only in the label -- so the label is the only
    //    discriminator available. An unrecognised label resolves to
    //    `unavailable`: refusing is safe, looking actionable is not.
    if (button.disabled) {
      return this.mainLabel() === window.variantStrings?.soldOut
        ? STICKY_ATC_STATE.SOLD_OUT
        : STICKY_ATC_STATE.UNAVAILABLE;
    }

    // 5. `product-form` leaves `aria-disabled` set after a failed add when the
    //    button contains a `.sold-out-message`. `buy-buttons.liquid` does not
    //    render one today, so this is the belt-and-braces path.
    if (button.getAttribute('aria-disabled') === 'true') return STICKY_ATC_STATE.ERROR;

    return STICKY_ATC_STATE.READY;
  }

  syncSubmitState() {
    if (!this.submitButton) return;

    const state = this.readSubmitState();
    this.state = state;
    this.element.dataset.stickyAtcState = state;
    this.submitButton.dataset.stickyAtcState = state;

    // `disabled` is reserved for the two states the shopper genuinely cannot act
    // on, and `aria-disabled` only for the in-flight add -- the one case where
    // the main form itself marks the button (`product-form.js`), so a keyboard
    // user does not lose focus mid-request. `error` is deliberately operable: it
    // is a message, not a disabled control.
    this.submitButton.disabled = STICKY_ATC_TERMINAL.has(state);
    this.submitButton.setAttribute('aria-disabled', state === STICKY_ATC_STATE.LOADING ? 'true' : 'false');

    const loading = state === STICKY_ATC_STATE.LOADING;
    this.submitButton.classList.toggle('loading', loading);
    if (this.submitSpinner) this.submitSpinner.classList.toggle('hidden', !loading);

    /*
     * Mirror the rendered label rather than choosing between two variantStrings.
     * The main button already carries the right translated string for every
     * state -- `choose_options`, `unavailable`, `sold_out`, `add_to_cart` -- and
     * no local derivation can produce all of them without duplicating a
     * translation table that would then drift from the locale files.
     */
    const mainLabel = this.mainLabel();
    if (this.submitLabel && mainLabel) this.submitLabel.textContent = mainLabel;

    this.syncNotice(state);
  }

  /*
   * The reason, on screen, in the one surface that is on screen.
   *
   * Exactly one live region speaks per event. The form's
   * `.product-form__error-message-wrapper` is `role="alert"` and already owns the
   * announcement for a failed add; when the bar is mirroring *that* text it is
   * marked `aria-hidden` so the same sentence is not read twice. For the option
   * states the form says nothing at all -- neither `product-options-gate` nor
   * `setUnavailable` touches the error wrapper -- so the bar is the only announcer
   * and stays live.
   *
   * The bar is `visibility: hidden` until the main button scrolls out of view,
   * and a live region inside a non-rendered subtree does not announce. That is the
   * intent: the shopper is not looking at the bar, so the bar must not interrupt.
   * When the bar slides in, the reason is already visible in it.
   */
  syncNotice(state) {
    if (!this.notice || !this.noticeLabel || !this.noticeDetail) return;

    const { label, detail, announce, action } = this.noticeContent(state);

    // `aria-hidden` is set before the text: a live region evaluates its
    // accessibility state at the moment the content changes, so writing first
    // would announce text under the *previous* state's policy.
    this.notice.setAttribute('aria-hidden', announce ? 'false' : 'true');

    // Compared, not assigned. Re-assigning an identical string still counts as a
    // character-data mutation, which would re-announce the same sentence every
    // time an unrelated observer fires.
    if (this.noticeLabel.textContent !== label) this.noticeLabel.textContent = label;
    if (this.noticeDetail.textContent !== detail) this.noticeDetail.textContent = detail;

    if (this.noticeAction) {
      const actionLabel = action ? window.variantStrings?.chooseOptions || '' : '';
      if (this.noticeAction.textContent !== actionLabel) this.noticeAction.textContent = actionLabel;
      this.noticeAction.toggleAttribute('hidden', !action);
    }

    this.noticeRow?.toggleAttribute('hidden', !label && !detail && !action);
  }

  noticeContent(state) {
    const strings = window.variantStrings || {};

    switch (state) {
      case STICKY_ATC_STATE.CHOOSE: {
        // Name the groups still outstanding. "Choose options" alone leaves the
        // shopper guessing which control is missing, and the picker has just
        // scrolled off screen.
        return {
          label: window.variantStrings?.selectOptions || 'Select',
          detail: this.pendingOptionNames().join(', '),
          announce: true,
          // The submit button already navigates to the picker in this state; a
          // second control carrying the same label would only duplicate it.
          action: false,
        };
      }
      case STICKY_ATC_STATE.UNAVAILABLE:
      case STICKY_ATC_STATE.SOLD_OUT: {
        // Both are terminal for this combination and both are escapable by
        // changing an option, but a `disabled` button cannot receive the click
        // that would do it -- so the notice carries the route back.
        return {
          label: this.mainLabel() || strings.unavailable || '',
          detail: this.selectedOptionValues(),
          announce: true,
          action: true,
        };
      }
      case STICKY_ATC_STATE.ERROR: {
        const formError = this.formErrorMessage();
        return {
          label: formError || this.cartErrorFallback || window.cartStrings?.error || '',
          detail: '',
          // The form's `role="alert"` speaks when it has the text; the bar speaks
          // only for the failures the form never displayed (a network error).
          announce: !formError,
          action: false,
        };
      }
      case STICKY_ATC_STATE.LOADING:
        // Say nothing: the spinner is the message, and text here would be
        // announced over it.
        return { label: '', detail: '', announce: false, action: false };
      default:
        return { label: '', detail: '', announce: false, action: false };
    }
  }

  /*
   * The picker's option groups, in DOM order.
   *
   * Grouped by the control `name`, which `product-variant-options.liquid` builds
   * from the option name and position. Controls are de-duplicated by value
   * because `HTMLUpdateUtility.viewTransition` leaves the outgoing
   * `variant-selects` in the DOM, hidden, for 500ms after every option change --
   * counting both copies makes a one-value group look like a two-value one.
   */
  static optionGroups() {
    const picker = document.querySelector('main variant-selects, main variant-radios');
    const groups = new Map();
    if (!picker) return [];

    const label = (control) =>
      // The human option name: `data-option-name` on the input (or its wrapping label). Falls back to the
      // internal `name` (`Size-1 `, which carries a trailing space), stripped to `Size`.
      control.dataset?.optionName ||
      control.closest('label')?.dataset?.optionName ||
      control.name?.replace(/-\d+\s*$/, '') ||
      '';

    const groupFor = (name) => {
      if (!groups.has(name)) groups.set(name, { name, label: '', controls: [], values: [] });
      return groups.get(name);
    };

    picker.querySelectorAll('input[type="radio"][name]').forEach((input) => {
      const group = groupFor(input.name);
      group.label ||= label(input);
      if (group.controls.some((existing) => existing.value === input.value && existing !== input)) return;
      group.controls.push(input);
      if (!group.values.includes(input.value)) group.values.push(input.value);
    });

    picker.querySelectorAll('select[name]').forEach((select) => {
      // A select *is* its whole group, so it replaces rather than appends.
      const group = groupFor(select.name);
      group.label ||= label(select);
      group.controls = [select];
      group.values = [...select.options]
        .map((option) => option.value)
        .filter((value) => value !== '');
    });

    return [...groups.values()];
  }

  static hasSelection(group) {
    return group.controls.some((control) =>
      control.tagName === 'SELECT' ? control.value !== '' : control.checked
    );
  }

  /* Names of the multi-value groups with nothing chosen: what still has to be picked. */
  pendingOptionNames() {
    const labels = new Set();
    StickyATC.optionGroups().forEach((group) => {
      // A group with a single possible value is not a choice -- the Liquid leaves
      // it pre-selected -- so it never counts as outstanding, exactly as in
      // `product-options-gate`.
      if (group.values.length <= 1) return;
      if (StickyATC.hasSelection(group)) return;
      labels.add(group.label);
    });
    return [...labels];
  }

  /* The combination the shopper has actually chosen, as display values. */
  selectedOptionValues() {
    const values = [];
    StickyATC.optionGroups().forEach((group) => {
      group.controls.forEach((control) => {
        if (control.tagName === 'SELECT') {
          if (!control.value) return;
          const option = [...control.options].find((candidate) => candidate.value === control.value);
          const text = (option?.textContent || '').trim() || control.value;
          if (!values.includes(text)) values.push(text);
        } else if (control.checked && !values.includes(control.value)) {
          values.push(control.value);
        }
      });
    });
    return values.join(' / ');
  }

  syncPrice() {
    if (!this.mainPrice || !this.price) return;
    this.price.innerHTML = this.mainPrice.innerHTML;
    // The main block renders at price--large; the bar does not.
    this.price.querySelectorAll('.price--large').forEach((el) => el.classList.remove('price--large'));
  }

  /*
   * The resolved title from the picker's server payload. Reading it from the JSON rather than
   * the rendered text matters on a gated first render: Liquid deliberately prints the
   * "Choose options" prompt there, so the rendered text is not a variant title.
   */
  readVariantTitle() {
    if (!this.variantTitle) return '';
    try {
      const variant = JSON.parse(
        document.querySelector('main variant-selects script[data-selected-variant]')?.textContent
      );
      if (variant?.title) return variant.title;
    } catch {
      /* no picker payload; fall through to the rendered text */
    }
    return this.variantTitle.textContent.trim();
  }

  syncVariantTitle() {
    if (!this.variantTitle) return;
    this.variantTitle.textContent = this.gateOpen
      ? this.variantTitle.dataset.gateLabel
      : this.currentVariantTitle;
  }

  /*
   * Scrolls back to the option picker and focuses the first control of the first
   * group the shopper still has to choose. Focusing the *outstanding* group
   * rather than the first control in the DOM matters once they have already
   * picked a size: the shopper has just been told to choose options, so focus
   * belongs on the option that is actually missing.
   */
  scrollToOptions() {
    const picker = document.querySelector(
      'main variant-selects, main variant-radios, .product-form__input'
    );
    if (!picker) return;
    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    picker.scrollIntoView({ behavior: reduceMotion ? 'auto' : 'smooth', block: 'center' });

    const outstanding = StickyATC.optionGroups().find(
      (group) => group.values.length > 1 && !StickyATC.hasSelection(group)
    );
    const fallback = picker.querySelector('input:not([type="hidden"]):not(:checked), select');
    const control = outstanding?.controls[0] || fallback;
    if (control) {
      window.setTimeout(() => control.focus({ preventScroll: true }), reduceMotion ? 0 : 350);
    }
  }
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => StickyATC.init(), { once: true });
} else {
  StickyATC.init();
}
